import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * MorphText, a keyed blur-crossfade wrapper for text that changes in place.
 *
 * COPIED, not imported, from pages/QuickStudyPage/GoalsCard/MorphText.tsx (plan
 * Wave 3): the questionnaire builder is a different product surface and must
 * not couple to the Quick Study page tree. The two are free to drift.
 *
 * Keying the inner element on `value` forces a remount on every change, so the
 * OLD text blurs and fades out while the NEW text blurs and fades in over the
 * `transition-smooth` curve, instead of snapping in place when an agent refine
 * lands on the objective. `prefers-reduced-motion` swaps instantly (no blur, no
 * fade) per the repo's motion rule.
 */

// Self-contained reduced-motion probe, mirroring the source component: the row
// must stay free of any coupling just to read one media query.
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  return reduced;
}

export interface MorphTextProps {
  value: string;
  as?: "p" | "span";
  className?: string;
}

export function MorphText(props: MorphTextProps) {
  const { value, as = "p", className } = props;

  return (
    <MorphTextFrame key={value} value={value} as={as} className={className} />
  );
}

interface MorphTextFrameProps {
  value: string;
  as: "p" | "span";
  className?: string;
}

function MorphTextFrame(props: MorphTextFrameProps) {
  const { value, as, className } = props;

  const reducedMotion = usePrefersReducedMotion();
  const [settled, setSettled] = useState(reducedMotion);

  useEffect(() => {
    if (reducedMotion) {
      setSettled(true);
      return;
    }
    setSettled(false);
    const raf = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed remount on `value` already restarts this effect
  }, [reducedMotion]);

  const Tag = as;

  return (
    <Tag
      className={cn(
        !reducedMotion && "transition-smooth",
        !reducedMotion && (settled ? "opacity-100 blur-none" : "opacity-0 blur-sm"),
        className,
      )}
    >
      {value}
    </Tag>
  );
}
